const EntityService = require("../services/entityService").default;
const UtilDateTime = require("./UtilDateTime").default;

class UtilRelatorioOSPDF {
  static logoCache = null;

  // ----------------------------------------------------------------
  // Logo
  // ----------------------------------------------------------------
  static async carregarLogo() {
    if (this.logoCache) return this.logoCache;
    try {
      const response = await fetch("/img/logo-delta-ducon.png");
      if (!response.ok) return null;
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => { this.logoCache = reader.result; resolve(this.logoCache); };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------
  static formatarData(valor) {
    if (!valor) return "";
    try {
      const d = new Date(valor);
      if (isNaN(d.getTime())) return String(valor).slice(0, 10);
      return d.toLocaleDateString("pt-BR");
    } catch {
      return String(valor).slice(0, 10);
    }
  }

  static formatarMoeda(valor) {
    const n = Number(valor || 0);
    return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  static extrairIdRelacionamento(valor) {
    if (!valor) return "";
    if (typeof valor === "string") return valor;
    if (Array.isArray(valor)) return this.extrairIdRelacionamento(valor[0]);
    if (typeof valor === "object") {
      return valor.id || valor._id || valor.value || "";
    }
    return "";
  }

  // ----------------------------------------------------------------
  // Enriquecimento dos dados
  // ----------------------------------------------------------------
  static async enriquecerOS(os = {}) {
    const dados = { ...os };
    const osId = dados.id || dados._id;

    // Busca OS completa do servidor para pegar childrens com os itens
    if (osId) {
      try {
        const osService = new EntityService("OrdemDeServico");
        const resp = await osService.getById(osId);
        const osCompleto = resp?.data?.data;
        if (osCompleto && typeof osCompleto === "object") {
          const current = osCompleto._currentData || {};
          Object.assign(dados, current);
          dados._childrens = osCompleto.childrens || {};
        }
      } catch (e) {
        console.error("[UtilRelatorioOSPDF] Erro ao buscar OS completa:", e);
      }
    }

    // Itens da OS via múltiplas estratégias
    let itens = [];
    const listaItensOS = Array.isArray(dados.itens)
      ? dados.itens
      : (Array.isArray(dados.items) ? dados.items : []);
    
    // Estratégia 1: childrens.OrdemDeServicoItens
    if (Array.isArray(dados._childrens?.OrdemDeServicoItens)) {
      itens = dados._childrens.OrdemDeServicoItens;
      console.log("[UtilRelatorioOSPDF] Itens encontrados em childrens.OrdemDeServicoItens:", itens.length);
    } 
    // Estratégia 2: childrens.ordemDeServicoItens (camelCase)
    else if (Array.isArray(dados._childrens?.ordemDeServicoItens)) {
      itens = dados._childrens.ordemDeServicoItens;
      console.log("[UtilRelatorioOSPDF] Itens encontrados em childrens.ordemDeServicoItens:", itens.length);
    }
    // Estratégia 3: itens/items no próprio registro (objetos)
    else if (Array.isArray(listaItensOS) && listaItensOS.length > 0 && typeof listaItensOS[0] === "object") {
      itens = listaItensOS;
      console.log("[UtilRelatorioOSPDF] Itens encontrados em dados.itens/items (objetos):", itens.length);
    }
    // Estratégia 4: lista de ids em itens/items -> busca individual pelo schema legado correto
    else if (Array.isArray(listaItensOS) && listaItensOS.length > 0 && typeof listaItensOS[0] === "string") {
      try {
        const itemService = new EntityService("OrdemDeServicoItens");
        const resultados = await Promise.allSettled(
          listaItensOS.map((id) => itemService.getById(id))
        );
        itens = resultados
          .filter((r) => r.status === "fulfilled")
          .map((r) => r.value?.data?.data?._currentData || r.value?.data?.data || null)
          .filter(Boolean);

        const falhas = resultados.filter((r) => r.status === "rejected").length;
        console.log("[UtilRelatorioOSPDF] Itens carregados por ids:", itens.length, "falhas:", falhas);
      } catch (e) {
        console.error("[UtilRelatorioOSPDF] Erro ao buscar itens da OS por ids:", e);
      }
    }
    
    itens = itens
      .map((item) => item?._currentData || item)
      .filter(Boolean)
      .sort((a, b) => {
        const sa = Number(a?.sequencia);
        const sb = Number(b?.sequencia);
        if (!Number.isNaN(sa) && !Number.isNaN(sb)) return sa - sb;
        return String(a?.sequencia || "").localeCompare(String(b?.sequencia || ""), "pt-BR", { numeric: true });
      });

    console.log("[UtilRelatorioOSPDF] Total de itens carregados:", itens.length);
    dados._itens = itens;

    // Dados do JOB
    dados._jobCodigo = dados._jobCodigo || dados.jobCodigo || dados.jobCode || "";
    dados._clienteNome = dados._clienteNome || dados.clienteNome || dados.clienteRazaoSocial || "";
    dados._salesOrderCodigo = dados._salesOrderCodigo || dados.solicitacaoDeOrcamentoCodigo || dados.salesOrderCodigo || "";

    const jobId = this.extrairIdRelacionamento(dados.job);
    if (jobId && typeof jobId === "string") {
      try {
        const jobService = new EntityService("Project");
        const resp = await jobService.getById(jobId);
        const job = resp?.data?.data?._currentData || resp?.data?.data || {};
        dados._jobCodigo = job.codigo || job.code || dados._jobCodigo || "";
        dados._clienteNome = job.clienteNome || job.cliente?.nomeFantasia || job.cliente?.razaoSocial || dados._clienteNome || "";
        dados._salesOrderCodigo = job.solicitacaoDeOrcamentoCodigo || job.salesOrderCode || job.orcamentoCodigo || dados._salesOrderCodigo || "";
      } catch (e) {
        console.warn("[UtilRelatorioOSPDF] Erro ao buscar JOB:", e);
      }
    }

    // Nome do coordenador
    const coordId = dados.coordenadorDeProjetos;
    const coordIdStr = typeof coordId === "object" ? coordId?.id || coordId?._id : coordId;
    if (coordIdStr && typeof coordIdStr === "string") {
      try {
        const colabService = new EntityService("Collaborator");
        const resp = await colabService.getById(coordIdStr);
        const colab = resp?.data?.data?._currentData || resp?.data?.data || {};
        dados._coordenadorNome = colab.nome || colab.name || colab.nomeCompleto || "";
      } catch {
        dados._coordenadorNome = "";
      }
    } else if (typeof coordId === "object" && coordId?.nome) {
      dados._coordenadorNome = coordId.nome;
    }

    // OS mãe (para coluna OS Filha)
    dados._osMaeCodigo = dados.osMaeCodigo || "";
    if (!dados._osMaeCodigo && dados.osMae) {
      try {
        const osMaeService = new EntityService("OrdemDeServico");
        const respMae = await osMaeService.getById(dados.osMae);
        const osMae = respMae?.data?.data?._currentData || respMae?.data?.data || {};
        dados._osMaeCodigo = osMae.codigo || "";
      } catch {
        dados._osMaeCodigo = "";
      }
    }

    return dados;
  }

  // ----------------------------------------------------------------
  // Definição do documento pdfmake
  // ----------------------------------------------------------------
  static criarDefinicaoPDF(dados, logo) {
    const AZUL = "#004a8f";
    const BRANCO = "#ffffff";
    const CINZA_LINHA = "#d0d7e3";
    const FUNDO_LINHA_PAR = "#f2f5fb";

    const lineSep = {
      canvas: [{ type: "line", x1: 0, y1: 0, x2: 770, y2: 0, lineWidth: 0.7, lineColor: AZUL }],
      margin: [0, 8, 0, 8],
    };

    // ---- Dados ----
    const osCodigo        = dados.codigo || dados.code || "";
    const osTipo          = dados.osTipo || "";
    const descricao       = dados.descricao || dados.projetoDescricao || "";
    const jobCodigo       = dados._jobCodigo || "";
    const clienteNome     = dados._clienteNome || "";
    const coordenadorNome = dados._coordenadorNome || "";
    const salesOrderCod   = dados._salesOrderCodigo || "";
    const osQuantidade    = String(dados.osQuantidade ?? "");
    const montagem        = dados.possuiMontagem ? "SIM" : "NÃO";
    const detalhamento    = dados.possuiDetalhamento ? "SIM" : "NÃO";
    const dataSolicitacao = this.formatarData(dados.dataSolicitacao);

    const fabricacaoPrev    = this.formatarData(dados.dataFabricacaoPrevista);
    const fabricacaoEfet    = this.formatarData(dados.dataFabricacao);
    const encerramentoPrev  = this.formatarData(dados.dataEncerramentoPrevista);
    const encerramentoEfet  = this.formatarData(dados.dataEncerramento);
    const montagemPrev      = this.formatarData(dados.dataMontagemPrevista);
    const montagemEfet      = this.formatarData(dados.dataMontagem);
    const detalhamentoPrev  = this.formatarData(dados.dataDetalhamentoPrevista);
    const detalhamentoEfet  = this.formatarData(dados.dataDetalhamento);

    const dataEmissao = new Date().toLocaleDateString("pt-BR");

    // ---- Helpers de célula ----
    const campo = (label, valor) => ({
      text: [{ text: `${label}: `, bold: true, fontSize: 7.5 }, { text: valor || "—", fontSize: 8 }],
    });

    // ---- Header da tabela de itens ----
    const thCell = (text, align = "left") => ({
      text, bold: true, fontSize: 7.5,
      color: BRANCO, fillColor: AZUL,
      alignment: align,
      margin: [3, 4, 3, 4],
    });

    const tabelaHeader = [[
      thCell("Seq", "center"),
      thCell("Quant", "right"),
      thCell("UM", "center"),
      thCell("Item", "center"),
      thCell("Descrição"),
      thCell("Dest", "center"),
      thCell("Lib.CCO", "center"),
      thCell("Dt.Necess.", "center"),
      thCell("St", "center"),
      thCell("Resp", "center"),
      thCell("Liber", "center"),
      thCell("OS Filha", "center"),
    ]];

    // ---- Linhas de itens ----
    const itens = Array.isArray(dados._itens) ? dados._itens : [];

    const tabelaRows = itens.length === 0
      ? [[{
          text: "Nenhum item cadastrado nesta OS.",
          colSpan: 12, alignment: "center", italic: true,
          fontSize: 8, margin: [0, 6, 0, 6], color: "#666666",
        }, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}]]
      : itens.map((item, idx) => {
          const bg = idx % 2 === 0 ? BRANCO : FUNDO_LINHA_PAR;
          const d = item._currentData || item;

          // Tenta extrair item de múltiplas formas
          let itemRef = {};
          if (d.item?._currentData) itemRef = d.item._currentData;
          else if (d.item && typeof d.item === "object") itemRef = d.item;
          else if (typeof d.item === "string") itemRef = { codigo: d.item };

          // Tenta extrair unidade de múltiplas formas
          let unidadeRef = {};
          if (d.unidade?._currentData) unidadeRef = d.unidade._currentData;
          else if (d.unidade && typeof d.unidade === "object") unidadeRef = d.unidade;
          else if (typeof d.unidade === "string") unidadeRef = { sigla: d.unidade };

          const desenhos = [d.desenhoFabricacao, d.desenhoMontagem]
            .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
            .map((v) => String(v).trim());

          const descricaoBase = itemRef.descricao || d.itemDescricao || d.descricao || "";
          const descricaoCompleta = desenhos.length > 0
            ? `${descricaoBase}\nDesenho: ${desenhos.join(" / ")}`
            : descricaoBase;

          const cell = (text, align = "left", noWrap = true) => ({
            text: String(text ?? ""),
            fontSize: 7.2,
            alignment: align,
            fillColor: bg,
            margin: [3, 2, 3, 2],
            noWrap,
          });

          return [
            cell(d.sequencia ?? (idx + 1), "center"),
            cell(Number(d.quantidade || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 }), "right"),
            cell(d.unidadeSigla || "", "center"),
            cell(d.itemCodigo || "", "center"),
            cell(descricaoCompleta, "left", false),
            cell(d.destino || "", "center"),
            cell(this.formatarData(d.dataSolicitacao), "center"),
            cell(this.formatarData(d.dataNecessidade), "center"),
            cell(d.status || "", "center"),
            cell("", "center"),
            cell(this.formatarData(d.dataLiberacaoCompras), "center"),
            cell(dados._osMaeCodigo || "", "center"),
          ];
        });

    // ---- Linha de datas ----
    const linhasDatas = [
      [
        campo("Fabricação Prev", fabricacaoPrev),
        campo("Fabricação Efet", fabricacaoEfet),
        campo("Encerramento Prev", encerramentoPrev),
        campo("Encerramento Efet", encerramentoEfet),
      ],
    ];
    if (dados.possuiMontagem || dados.possuiDetalhamento) {
      linhasDatas.push([
        campo("Montagem Prev", dados.possuiMontagem ? montagemPrev : "—"),
        campo("Montagem Efet", dados.possuiMontagem ? montagemEfet : "—"),
        campo("Detalhamento Prev", dados.possuiDetalhamento ? detalhamentoPrev : "—"),
        campo("Detalhamento Efet", dados.possuiDetalhamento ? detalhamentoEfet : "—"),
      ]);
    }

    // ---- Documento final ----
    return {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [25, 25, 25, 35],
      images: { logoImage: logo },

      footer: (currentPage, pageCount) => ({
        text: `JOB ${jobCodigo || "-"} - OS ${osCodigo || "-"}   —   Emitido em: ${dataEmissao}   —   Página ${currentPage} de ${pageCount}   —   ERP Deltaducon`,
        alignment: "center", fontSize: 7, color: "#888888",
        margin: [0, 0, 0, 10],
      }),

      content: [
        // ---- CABEÇALHO ----
        {
          columns: [
            { width: 160, image: "logoImage", fit: [150, 55], alignment: "left" },
            {
              width: "*",
              stack: [
                { text: "Lista de Materiais", bold: true, fontSize: 18, color: AZUL, alignment: "right" },
                { text: `OS: ${osCodigo}   JOB: ${jobCodigo || ""}`, bold: true, fontSize: 12, color: "#111111", alignment: "right" },
              ],
              margin: [0, 4, 0, 0],
            },
          ],
          margin: [0, 0, 0, 4],
        },

        lineSep,

        // ---- BLOCO DE INFORMAÇÕES PRINCIPAIS ----
        {
          table: {
            widths: ["*"],
            body: [[{
              columns: [
                {
                  width: "33%",
                  stack: [
                    campo("OS", osCodigo),
                    campo("JOB", jobCodigo),
                    campo("Proposta", salesOrderCod),
                    campo("Data Solicitação", dataSolicitacao),
                  ],
                  margin: [6, 5, 6, 5],
                },
                {
                  width: "34%",
                  stack: [
                    campo("Cliente", clienteNome),
                    campo("Coordenador", coordenadorNome),
                    campo("Tipo OS", osTipo),
                    campo("Quantidade", osQuantidade),
                  ],
                  margin: [6, 5, 6, 5],
                },
                {
                  width: "33%",
                  stack: [
                    campo("Montagem", montagem),
                    campo("Detalhamento", detalhamento),
                    campo("Descrição", descricao),
                  ],
                  margin: [6, 5, 6, 5],
                },
              ],
            }]],
          },
          layout: {
            hLineWidth: () => 0.3,
            vLineWidth: () => 0.3,
            hLineColor: () => CINZA_LINHA,
            vLineColor: () => CINZA_LINHA,
          },
          margin: [0, 0, 0, 4],
        },

        // ---- DATAS ----
        {
          table: {
            widths: ["25%", "25%", "25%", "25%"],
            body: linhasDatas.map((row) =>
              row.map((cel) => ({ ...cel, margin: [6, 3, 6, 3] }))
            ),
          },
          layout: {
            hLineWidth: () => 0.3,
            vLineWidth: () => 0.3,
            hLineColor: () => CINZA_LINHA,
            vLineColor: () => CINZA_LINHA,
          },
          margin: [0, 0, 0, 10],
        },

        // ---- TÍTULO ITENS ----
        {
          text: `Lista de Materiais  (${itens.length} item${itens.length !== 1 ? "s" : ""})`,
          bold: true, fontSize: 11, color: AZUL, margin: [0, 0, 0, 6],
        },

        // ---- TABELA DE ITENS ----
        {
          table: {
            widths: [24, 34, 24, 42, "*", 30, 40, 40, 22, 26, 36, 34],
            body: [...tabelaHeader, ...tabelaRows],
          },
          layout: {
            hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.3,
            vLineWidth: () => 0.3,
            hLineColor: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? AZUL : CINZA_LINHA,
            vLineColor: () => CINZA_LINHA,
          },
        },
      ],
    };
  }

  // ----------------------------------------------------------------
  // Ponto de entrada principal
  // ----------------------------------------------------------------
  static async gerarRelatorioOS(os = {}) {
    const pdfMake = require("pdfmake/build/pdfmake.js");
    const pdfFonts = await require("pdfmake/build/vfs_fonts.js");
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    const logo = await this.carregarLogo();
    if (!logo) throw new Error("[UtilRelatorioOSPDF] Não foi possível carregar o logo.");

    const dadosEnriquecidos = await this.enriquecerOS(os);
    const def = this.criarDefinicaoPDF(dadosEnriquecidos, logo);

    const nomeArquivo = `OS_${dadosEnriquecidos.codigo || "relatorio"}_JOB_${dadosEnriquecidos._jobCodigo || ""}.pdf`
      .replace(/\s+/g, "_");

    pdfMake.createPdf(def).download(nomeArquivo);
  }

  static async gerarRelatorioOSLote(listaOS = []) {
    const lista = Array.isArray(listaOS) ? listaOS.filter(Boolean) : [];
    if (!lista.length) {
      throw new Error("[UtilRelatorioOSPDF] Nenhuma OS informada para geração em lote.");
    }

    if (lista.length === 1) {
      await this.gerarRelatorioOS(lista[0]);
      return;
    }

    const pdfMake = require("pdfmake/build/pdfmake.js");
    const pdfFonts = await require("pdfmake/build/vfs_fonts.js");
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    const logo = await this.carregarLogo();
    if (!logo) throw new Error("[UtilRelatorioOSPDF] Não foi possível carregar o logo.");

    const dadosLote = [];
    for (const os of lista) {
      const enriquecida = await this.enriquecerOS(os);
      dadosLote.push(enriquecida);
    }

    const content = [];
    dadosLote.forEach((dados, index) => {
      const defOS = this.criarDefinicaoPDF(dados, logo);
      content.push({
        stack: defOS.content,
        pageBreak: index === 0 ? undefined : "before",
      });
    });

    const dataEmissao = new Date().toLocaleDateString("pt-BR");
    const definicaoLote = {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [25, 25, 25, 35],
      images: { logoImage: logo },
      footer: (currentPage, pageCount) => ({
        text: `Relatório Lista de Materiais   —   Emitido em: ${dataEmissao}   —   Página ${currentPage} de ${pageCount}   —   ERP Deltaducon`,
        alignment: "center", fontSize: 7, color: "#888888",
        margin: [0, 0, 0, 10],
      }),
      content,
    };

    const nomeArquivo = `ListaMateriais_${lista.length}_OS.pdf`;
    pdfMake.createPdf(definicaoLote).download(nomeArquivo);
  }
}

module.exports = UtilRelatorioOSPDF;
